import React, { useEffect } from 'react';
import { ChartCard, HorizontalBars, InfoTooltip } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';
import {
  type RelativePerformance,
  getRelativePerformance,
} from '../-helpers/getRelativePerformance';
import { useSortedChartData } from '../-hooks/useSortedChartData';
import { OptionalKey } from '../route';

export const KPIImpact_PatientsTreatedFragment = gql(/* GraphQL */ `
  fragment KPIImpact_PatientsTreatedFragment on HealthcareIndicators {
    patientsTreated {
      trimmedAvg
      isDataSuppressed
    }
    patientsTreated_n {
      trimmedAvg
      isDataSuppressed
    }
    patientsTreated_pace {
      trimmedAvg
      isDataSuppressed
    }
  }
`);

type PatientsTreatedProps = {
  className?: string;
  indicators: FragmentType<typeof KPIImpact_PatientsTreatedFragment>;
  viewerIndicators?: FragmentType<typeof KPIImpact_PatientsTreatedFragment>;
  investmentLevel: OptionalKey;
  onEvaluatePerformance: (key: string, performance: RelativePerformance) => void;
};

export function PatientsTreated({
  indicators,
  viewerIndicators,
  investmentLevel,
  onEvaluatePerformance,
  className,
}: PatientsTreatedProps) {
  const benchmarkData = useFragment(KPIImpact_PatientsTreatedFragment, indicators);
  const viewerData = useFragment(KPIImpact_PatientsTreatedFragment, viewerIndicators);

  const benchmarkDataMap = {
    company: benchmarkData.patientsTreated,
    investment: benchmarkData.patientsTreated_n,
    rate_of_change: benchmarkData.patientsTreated_pace,
  };

  const viewerDataMap = {
    company: viewerData?.patientsTreated,
    investment: viewerData?.patientsTreated_n,
    rate_of_change: viewerData?.patientsTreated_pace,
  };

  const selectedBenchmarkData = benchmarkDataMap[investmentLevel];
  const selectedViewerData = viewerDataMap[investmentLevel];

  const { t, title, titleTooltipKPI } = useChartTranslations('patientsTreated');

  const getDisplayValue = (value?: number | null): string => {
    if (value == null) return '';
    const strValue = value.toLocaleString('en', { maximumFractionDigits: 0 });
    return t('displayValue', { value: strValue }) ?? strValue;
  };

  const { sortedChartData } = useSortedChartData({
    selectedBenchmarkData,
    selectedViewerData,
    getDisplayValue,
  });

  const relativePerformance = getRelativePerformance(
    selectedBenchmarkData?.trimmedAvg,
    selectedViewerData?.trimmedAvg
  );

  useEffect(() => {
    onEvaluatePerformance('patientsTreated', relativePerformance);
  }, [onEvaluatePerformance, relativePerformance]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header className="flex items-center justify-between">
        <ChartCard.Title>
          {title}
          {titleTooltipKPI && <InfoTooltip>{titleTooltipKPI}</InfoTooltip>}
        </ChartCard.Title>
        {/* <Button variant="outline" className="shrink-0 font-semibold uppercase">
          view details
        </Button> */}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={selectedBenchmarkData.isDataSuppressed}>
        <HorizontalBars
          min={Math.min(
            0,
            selectedViewerData?.trimmedAvg ?? 0,
            selectedBenchmarkData.trimmedAvg ?? 0
          )}
          max={Math.max(
            100,
            selectedViewerData?.trimmedAvg ?? 0,
            selectedBenchmarkData.trimmedAvg ?? 0
          )}
          showInlineDisplayValue={true}
        >
          {sortedChartData.map((data, index) => (
            <HorizontalBars.Item
              key={index}
              label={data.label}
              value={data.value}
              displayValue={data.displayValue}
              color={data.color}
              isDashed={data.isDashed}
            />
          ))}
        </HorizontalBars>

        {/* <Link href="/impact-kpis/land-managed" className="mt-5 lg:hidden">
          <Button variant="outline" size="small" className="shrink-0 font-semibold uppercase">
            view details
          </Button>
        </Link> */}
      </ChartCard.Body>
    </ChartCard>
  );
}
